import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { AppsTab } from "./tabs/AppsTab.js";
import { SchedulesTab } from "./tabs/SchedulesTab.js";
import { HistoryTab } from "./tabs/HistoryTab.js";
import { ActiveReloadsTab } from "./tabs/ActiveReloadsTab.js";
import { ActivityTab } from "./tabs/ActivityTab.js";

type TabKey = "apps" | "schedules" | "history" | "active" | "activity";

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: "apps", label: "Apps" },
  { key: "schedules", label: "Schedules" },
  { key: "history", label: "History" },
  { key: "active", label: "Active Reloads" },
  { key: "activity", label: "Activity" },
];

function isTabKey(v: string | null): v is TabKey {
  return (
    v === "apps" ||
    v === "schedules" ||
    v === "history" ||
    v === "active" ||
    v === "activity"
  );
}

export function SchedulerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const current = useMemo<TabKey>(() => {
    const raw = searchParams.get("tab");
    return isTabKey(raw) ? raw : "apps";
  }, [searchParams]);

  function setTab(next: TabKey) {
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  }

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-8 sm:px-6 sm:py-10">
      <header className="mb-8">
        <p className="eyebrow">Scheduler</p>
        <h1 className="heading-underline mt-2 text-2xl font-extrabold uppercase tracking-[0.02em] text-ink">
          Scheduler
        </h1>
        <p className="mt-4 max-w-prose text-sm text-ink-soft">
          Browse apps, manage schedules, and watch reloads in flight.
        </p>
      </header>

      <div
        role="tablist"
        aria-label="Scheduler sections"
        className="mb-6 flex flex-wrap gap-2"
      >
        {tabs.map((t) => {
          const isActive = current === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${t.key}`}
              id={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={isActive ? "btn-primary-sm" : "btn-ghost-sm"}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`tabpanel-${current}`}
        aria-labelledby={`tab-${current}`}
      >
        {current === "apps" ? <AppsTab onSwitchTab={setTab} /> : null}
        {current === "schedules" ? <SchedulesTab onSwitchTab={setTab} /> : null}
        {current === "history" ? <HistoryTab /> : null}
        {current === "active" ? <ActiveReloadsTab /> : null}
        {current === "activity" ? <ActivityTab /> : null}
      </div>
    </div>
  );
}
